/**
 * LOCO — predictive search (Shopify Predictive Search API via Section Rendering).
 * Combobox pattern: input[role=combobox] + listbox of options.
 * If the request fails, the form still submits to /search.
 */
(() => {
  const LOCO = (window.LOCO = window.LOCO || {});

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results) return;
      this.cache = new Map();
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.results.id);

      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.input.value.trim()), 220);
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.closest('dialog')?.addEventListener('loco:dialog-close', () => this.reset(true));
    }

    async search(term) {
      if (!term) return this.reset();
      this.controller?.abort();
      this.controller = new AbortController();

      if (this.cache.has(term)) return this.render(this.cache.get(term));

      const url = new URL(LOCO.routes?.predictiveSearch || '/search/suggest', window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', this.dataset.types || 'product,collection,page,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');

      this.setAttribute('aria-busy', 'true');
      try {
        const doc = await LOCO.fetchSection(url.toString(), { signal: this.controller.signal });
        const html = doc.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
        this.cache.set(term, html);
        this.render(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.innerHTML = `<p class="predictive__status">${this.dataset.errorText || ''}</p>`;
        this.open(true);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.options = [...this.results.querySelectorAll('[role="option"]')];
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.open(true);
      const count = this.results.querySelector('[data-result-count]')?.dataset.resultCount;
      if (this.status && count !== undefined) this.status.textContent = count;
    }

    open(state) {
      this.results.hidden = !state;
      this.input.setAttribute('aria-expanded', String(state));
    }

    reset(clearInput = false) {
      this.controller?.abort();
      this.results.innerHTML = '';
      this.open(false);
      if (clearInput) this.input.value = '';
    }

    onKeydown(event) {
      if (!this.options?.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + dir + this.options.length) % this.options.length;
        this.options.forEach((opt, i) => opt.setAttribute('aria-selected', String(i === this.activeIndex)));
        const active = this.options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex > -1) {
        event.preventDefault();
        this.options[this.activeIndex].click();
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
